// components/CreateContactModal.jsx
import React, { useState, useEffect } from "react";
import { X, User, Loader, Search, Building2, MapPin } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateContactModal = ({ onClose, onSuccess, account }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    salutation: "",
    firstName: "",
    lastName: "",
    title: "",
    email: "",
    phone: "",
    accountId: account?.id || "",
    ownerId: "",
    reportsToId: "",
    department: "",
    fax: "",
    mobilePhone: "",
    mailingStreet: account?.billingStreet || "",
    mailingCity: account?.billingCity || "",
    mailingState: account?.billingState || "",
    mailingPostalCode: account?.billingPostalCode || "",
    mailingCountry: account?.billingCountry || "",
    hasOptedOutOfEmail: false,
    doNotCall: false,
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUsers();
    if (account?.id) fetchAccountContacts();
  }, [account?.id]);

  const fetchUsers = async () => {
    try {
      const response = await apiService.getUsers();
      setUsers(response.data || []);
      const currentUser = response.data.find(
        (u) => u.id === localStorage.getItem("userId"),
      );
      if (currentUser)
        setFormData((prev) => ({ ...prev, ownerId: currentUser.id }));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchAccountContacts = async () => {
    try {
      const accountData = await apiService.getAccount(account.id);
      setContacts(accountData.data.contacts || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const sanitizeFormData = (data) => {
    const sanitized = { ...data };
    ["accountId", "ownerId", "reportsToId"].forEach((field) => {
      if (sanitized[field] === "") sanitized[field] = null;
    });
    Object.keys(sanitized).forEach((key) => {
      if (sanitized[key] === undefined) delete sanitized[key];
    });
    return sanitized;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const sanitizedData = sanitizeFormData({
        ...formData,
        accountId: account?.id || formData.accountId,
      });
      await apiService.createContact(sanitizedData);
      onSuccess();
    } catch (error) {
      console.error("Error creating contact:", error);
      setError(
        error.response?.data?.message ||
          "Failed to create contact. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Theme helpers
  const inputClass = `w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 ${
    isDarkMode
      ? "bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `block text-sm font-medium mb-2.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;

  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: "right 0.5rem center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "1.5em 1.5em",
    paddingRight: "2.5rem",
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`rounded-2xl shadow-2xl border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-gradient-to-br from-[#0C0E12] to-[#0A0C10] border-gray-800/50"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`relative border-b p-8 ${
            isDarkMode
              ? "bg-gradient-to-r from-[#0C0E12] via-[#0E1015] to-[#0C0E12] border-gray-800/50"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          {isDarkMode && (
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent pointer-events-none" />
          )}
          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/20 flex items-center justify-center">
                  <User size={20} className="text-purple-400" />
                </div>
                <h2
                  className={`text-2xl font-semibold tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Create Contact
                </h2>
              </div>
              <p
                className={`text-sm font-light ml-13 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Add a new contact{account && ` for ${account.name}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`group w-9 h-9 rounded-lg border flex items-center justify-center transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-700/50 border-gray-700/50 hover:border-gray-600/50 text-gray-400 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-300 text-gray-500 hover:text-gray-900"
              }`}
            >
              <X
                size={18}
                className="group-hover:rotate-90 transition-transform duration-200"
              />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div
                className={`border rounded-xl p-4 animate-slideDown ${
                  isDarkMode
                    ? "bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <X size={16} className="text-red-400" />
                  </div>
                  <p
                    className={`text-sm font-medium ${isDarkMode ? "text-red-300" : "text-red-700"}`}
                  >
                    {error}
                  </p>
                </div>
              </div>
            )}

            {/* Account Info */}
            {account && (
              <div
                className={`rounded-xl p-5 border ${
                  isDarkMode
                    ? "bg-gradient-to-br from-gray-800/30 to-gray-900/20 border-gray-700/40"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Building2 size={20} className="text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-semibold text-lg mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {account.name}
                    </h3>
                    <div className="space-y-1.5">
                      {account.type && (
                        <div
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {account.type}
                        </div>
                      )}
                      {account.phone && (
                        <div
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {account.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information Section */}
            <div className="space-y-5">
              <h3
                className={`text-sm font-semibold uppercase tracking-wider flex items-center gap-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                <User size={16} className="text-gray-400" />
                Contact Information
              </h3>

              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Salutation</label>
                  <select
                    name="salutation"
                    value={formData.salutation}
                    onChange={handleChange}
                    className={`${inputClass} appearance-none cursor-pointer`}
                    style={selectStyle}
                  >
                    <option value="">Select...</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Dr.">Dr.</option>
                    <option value="Prof.">Prof.</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="First Name"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Last Name <span className="text-red-400 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Last Name"
                  />
                </div>
              </div>

              {/* Job Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. Sales Manager"
                  />
                </div>
                <div>
                  <label className={labelClass}>Department</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. Sales"
                  />
                </div>
              </div>

              {/* Contact Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="contact@example.com"
                  />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              {/* Additional Phones */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Mobile Phone</label>
                  <input
                    type="tel"
                    name="mobilePhone"
                    value={formData.mobilePhone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label className={labelClass}>Fax</label>
                  <input
                    type="tel"
                    name="fax"
                    value={formData.fax}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              {/* Owner and Reports To */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Contact Owner</label>
                  <select
                    name="ownerId"
                    value={formData.ownerId}
                    onChange={handleChange}
                    className={`${inputClass} appearance-none cursor-pointer`}
                    style={selectStyle}
                  >
                    <option value="">Select Owner...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Reports To</label>
                  <select
                    name="reportsToId"
                    value={formData.reportsToId}
                    onChange={handleChange}
                    className={`${inputClass} appearance-none cursor-pointer`}
                    style={selectStyle}
                  >
                    <option value="">Select Contact...</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.firstName} {contact.lastName}
                        {contact.title && ` - ${contact.title}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div
              className={`space-y-5 pt-4 border-t ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
            >
              <h3
                className={`text-sm font-semibold uppercase tracking-wider flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                <MapPin size={16} className="text-gray-400" />
                Mailing Address
              </h3>

              <div>
                <label className={labelClass}>Mailing Street</label>
                <textarea
                  name="mailingStreet"
                  value={formData.mailingStreet}
                  onChange={handleChange}
                  rows={2}
                  className={`${inputClass} resize-none`}
                  placeholder="Mailing Street"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Mailing City</label>
                  <input
                    type="text"
                    name="mailingCity"
                    value={formData.mailingCity}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Lincoln"
                  />
                </div>
                <div>
                  <label className={labelClass}>Mailing State/Province</label>
                  <input
                    type="text"
                    name="mailingState"
                    value={formData.mailingState}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="State/Province"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Mailing Zip/Postal Code</label>
                  <input
                    type="text"
                    name="mailingPostalCode"
                    value={formData.mailingPostalCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="LN4 4BU"
                  />
                </div>
                <div>
                  <label className={labelClass}>Mailing Country</label>
                  <input
                    type="text"
                    name="mailingCountry"
                    value={formData.mailingCountry}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>

            {/* Marketing Section */}
            <div
              className={`space-y-4 pt-4 border-t ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
            >
              <h3
                className={`text-sm font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Marketing
              </h3>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="hasOptedOutOfEmail"
                    checked={formData.hasOptedOutOfEmail}
                    onChange={handleChange}
                    className={`w-4 h-4 rounded text-purple-600 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer ${
                      isDarkMode
                        ? "border-gray-700 bg-gray-900/50"
                        : "border-gray-300 bg-white"
                    }`}
                  />
                  <span
                    className={`text-sm transition-colors group-hover:text-white ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    Email Opt Out
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="doNotCall"
                    checked={formData.doNotCall}
                    onChange={handleChange}
                    className={`w-4 h-4 rounded text-purple-600 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer ${
                      isDarkMode
                        ? "border-gray-700 bg-gray-900/50"
                        : "border-gray-300 bg-white"
                    }`}
                  />
                  <span
                    className={`text-sm transition-colors group-hover:text-white ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    Do Not Call
                  </span>
                </label>
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-5 py-3 border rounded-xl font-medium transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-300 text-gray-700"
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-5 py-3 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20 hover:shadow-purple-600/30"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #9333EA 0%, #6B21A8 100%)",
              }}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <User size={18} />
                  <span>Save</span>
                </>
              )}
            </button>
            <button
              type="button"
              disabled={loading}
              className={`px-5 py-3 border rounded-xl font-medium transition-all duration-200 disabled:opacity-50 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-300 text-gray-700"
              }`}
            >
              Save & New
            </button>
          </div>
          <div className="mt-3 text-right">
            <span className="text-red-400 text-xs">*</span>
            <span
              className={`text-xs ml-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              = Required Information
            </span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CreateContactModal;
